import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import JourneySectionHeader from "@/components/JourneySectionHeader";
import { JourneyProgressSection } from "@/apps/los/components/ProgressBar/JourneySectionProgress";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CaprionDealerApprovalProps {
    isBuilderMode?: boolean;
    onDone?: () => void;
    /** Passed by JourneyPage to every fullPageComponent — drives the header progress bar. */
    progressSections?: JourneyProgressSection[];
    currentPageId?: number | null;
}

interface CaprionData {
    sanctioned_amount?: number;
    tenure?: number;
    emi_amount?: number;
    roi?: number;
    disbursal_amount?: number;
    bank_account?: string;
}

const CaprionDealerApproval: React.FC<CaprionDealerApprovalProps> = ({
    isBuilderMode = false,
    onDone,
    progressSections,
    currentPageId,
}) => {
    const [validating, setValidating] = useState(false);
    const [approved, setApproved] = useState(false);
    const [rejected, setRejected] = useState(false);
    const [validationFailed, setValidationFailed] = useState(false);
    const [disbursalDisabled, setDisbursalDisabled] = useState(false);
    const [message, setMessage] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [caprionData, setCaprionData] = useState<CaprionData | null>(null);

    useEffect(() => {
        if (isBuilderMode) {
            return;
        }

        // Auto-trigger validation on mount
        handleValidateAndApprove();
    }, [isBuilderMode]);

    const handleValidateAndApprove = async () => {
        setValidating(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/caprion/validate-and-approve-dealer/`);
            
            if (response.data.success) {
                const isApproved = response.data.approved !== false;  // Default to true if not specified
                const displayMessage = response.data.message || (isApproved ? 'Loan Approved!' : 'Loan Rejected');
                
                setApproved(isApproved);
                setRejected(!isApproved);
                setMessage(displayMessage);
                setCaprionData(response.data.caprion_data);
                
                if (isApproved) {
                    toast.success(displayMessage);
                    // Auto-advance ONLY on approval after 2 seconds
                    if (onDone) {
                        setTimeout(() => {
                            onDone();
                        }, 2000);
                    }
                } else {
                    toast.error(displayMessage);
                    // Do NOT auto-advance on rejection - stay on this page
                    setValidating(false);
                }
            } else {
                // Check if disbursal is temporarily disabled (system throttle)
                if (response.data.disbursal_disabled) {
                    setDisbursalDisabled(true);
                    setMessage(response.data.message || 'System is temporarily busy. Please try again in a few hours.');
                    toast.error('System is temporarily busy. Please try again later.');
                // Check if validation failed
                } else if (response.data.validation_failed) {
                    setValidationFailed(true);
                    setValidationErrors(response.data.validation_errors || []);
                    setMessage('Validation Failed');
                    setCaprionData(response.data.caprion_data);
                    toast.error('Loan details validation failed');
                } else {
                    // On other failures, do NOT auto-advance - stay on this page
                    toast.error(response.data.error || 'Loan approval failed');
                }
                setValidating(false);
            }
        } catch (err: any) {
            console.error('Error validating and approving:', err);
            
            // Check if it's a validation failure (400 status with validation_failed flag)
            if (err.response?.status === 400 && err.response?.data?.validation_failed) {
                setValidationFailed(true);
                setValidationErrors(err.response.data.validation_errors || []);
                setMessage('Validation Failed');
                setCaprionData(err.response.data.caprion_data);
                toast.error('Loan details validation failed');
                setValidating(false);
            } else {
                // On other errors, do NOT auto-advance - stay on this page
                const errorMsg = err.response?.data?.error || err.message || 'Failed to process loan approval';
                toast.error(errorMsg);
                setValidating(false);
            }
        }
    };

    const formatCurrency = (amount?: number) => {
        if (!amount) return 'N/A';
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const formatPercentage = (rate?: number) => {
        if (!rate) return 'N/A';
        return `${rate}%`;
    };

    // Builder mode placeholder
    if (isBuilderMode) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
                <div className="max-w-2xl mx-auto">
                    <Card>
                        <CardHeader>
                            <CardTitle>Dealer Approval Validation</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground">
                                This page validates loan details with Caprion and approves for disbursal if everything matches.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
            {/* Header — page title + journey progress, same as every other page */}
            <JourneySectionHeader
                className="shrink-0"
                title="Validating Loan Details"
                progressSections={progressSections}
                currentPageId={currentPageId}
            />

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-3xl">
                    <CardHeader>
                        <CardTitle className="text-center text-2xl">
                            Loan Status
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Status Icon */}
                        <div className="flex justify-center">
                            {validating && <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />}
                            {!validating && approved && <CheckCircle className="h-16 w-16 text-intellend-green" />}
                            {!validating && disbursalDisabled && <Loader2 className="h-16 w-16 text-amber-400" />}
                            {!validating && !disbursalDisabled && (rejected || validationFailed) && <XCircle className="h-16 w-16 text-red-500" />}
                        </div>

                        {/* Status Message */}
                        <div className="text-center">
                            {validating && (
                                <p className="text-gray-600">
                                    Validating your Loan Details
                                </p>
                            )}
                            {!validating && approved && (
                                <div className="space-y-2">
                                    <p className="text-lg font-semibold text-intellend-green">
                                        {message}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Redirecting to final status...
                                    </p>
                                </div>
                            )}
                            {!validating && disbursalDisabled && (
                                <div className="space-y-4">
                                    <p className="text-lg font-semibold text-amber-700">
                                        Almost There!
                                    </p>
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        {message}
                                    </p>
                                </div>
                            )}
                            {!validating && rejected && (
                                <div className="space-y-2">
                                    <p className="text-lg font-semibold text-red-700">
                                        {message}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Your loan application has been rejected by the lender.
                                    </p>
                                </div>
                            )}
                            {!validating && validationFailed && (
                                <div className="space-y-2">
                                    <p className="text-lg font-semibold text-red-700">
                                        Validation failed
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Approved Loan Details */}
                        {approved && caprionData && (
                            <div className="bg-intellend-green/5 border border-intellend-green/20 rounded-lg p-4 space-y-3">
                                <h3 className="font-semibold text-intellend-green">Approved Loan Details</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-gray-600">Sanctioned Amount:</span>
                                        <p className="font-semibold">{formatCurrency(caprionData.sanctioned_amount)}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Tenure:</span>
                                        <p className="font-semibold">{caprionData.tenure} months</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">EMI Amount:</span>
                                        <p className="font-semibold">{formatCurrency(caprionData.emi_amount)}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Interest Rate:</span>
                                        <p className="font-semibold">{formatPercentage(caprionData.roi)}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Disbursal Amount:</span>
                                        <p className="font-semibold">{formatCurrency(caprionData.disbursal_amount)}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Bank Account:</span>
                                        <p className="font-semibold">{caprionData.bank_account || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CaprionDealerApproval;
